import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Text,
  Button,
  Input,
  Tooltip,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  TableCellLayout,
  Skeleton,
  SkeletonItem,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  createTableColumn,
  mergeClasses,
  type TableColumnDefinition,
  type TableColumnSizingOptions,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  EditRegular,
  CheckmarkRegular,
  DismissRegular,
  DiamondFilled,
  InfoFilled,
} from '@fluentui/react-icons';
import { useProgramChangesPageStyles } from '../../styles/ProgramChangesPage.styles';
import { exportToCsv } from '../../utils/exportToCsv';
import { compareString } from '../../utils/comparators';
import { formatCurrency } from '../../utils/formatters';
import { useMarketCategories } from '../../contexts/MarketCategoriesContext';
import type {
  MarketCategory,
  PendingMarketCategoryInfo,
  PendingThresholdsInfo,
} from '../../services/marketCategoriesService';
import { TIER_KEYS, TIER_LABELS, type TierKey } from '../../constants/tiers';
import {
  getFriendlyApiErrorMessage,
  logApiError,
} from '../../services/apiErrorMessages';
import { formatPendingTooltip as formatSharedPendingTooltip } from './formatPendingTooltip';
import { ProgramChangesLastUpdatedHeader } from './ProgramChangesLastUpdatedHeader';

interface ThresholdsByMarketTabProps {
  isLocked: boolean;
}

type ColumnId = 'category' | TierKey | 'actions';

interface Tier {
  key: TierKey;
  label: string;
  diamondClass: string;
}

const columnWeights: Record<ColumnId, number> = {
  category: 1.5,
  silver: 2,
  gold: 2,
  platinum: 2,
  actions: 0.8,
};

const columnMinWidths: Record<ColumnId, number> = {
  category: 160,
  silver: 130,
  gold: 130,
  platinum: 130,
  actions: 110,
};

const parseCurrency = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!/^-?\$?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(trimmed)) return null;
  const cleaned = trimmed.replace(/[$,]/g, '');
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
};

interface ThresholdDraftValidation {
  values: Record<TierKey, number> | null;
  error: string | null;
}

const validateThresholdDraft = (
  draft: Record<TierKey, string> | null,
): ThresholdDraftValidation => {
  if (!draft) return { values: null, error: null };

  const values = {} as Record<TierKey, number>;
  for (const key of TIER_KEYS) {
    const value = parseCurrency(draft[key]);
    if (value === null) {
      return {
        values: null,
        error: 'Enter a valid number for every threshold.',
      };
    }
    values[key] = value;
  }

  if (TIER_KEYS.some((key) => values[key] < 0)) {
    return {
      values: null,
      error: 'Thresholds cannot be negative.',
    };
  }

  if (!(values.silver < values.gold && values.gold < values.platinum)) {
    return {
      values: null,
      error: 'Thresholds must increase from Silver to Gold to Platinum.',
    };
  }

  return { values, error: null };
};

const formatPendingTooltip = (
  pending: PendingMarketCategoryInfo | PendingThresholdsInfo,
): string => {
  const isThresholdPending = 'previousThresholds' in pending;
  if (isThresholdPending) {
    return formatSharedPendingTooltip(pending, {
      updateSummary: 'threshold values updated',
    });
  }
  return formatSharedPendingTooltip(pending);
};

const formatThresholdPendingTooltip = (
  pending: PendingThresholdsInfo,
  tier: TierKey,
): string => {
  const when = (() => {
    const date = new Date(pending.createdAt);
    return isNaN(date.getTime()) ? pending.createdAt : date.toLocaleString('en-US');
  })();
  const who = pending.createdBy || 'unknown user';
  const previous = formatCurrency(pending.previousThresholds[tier]);
  return `Pending threshold change for ${TIER_LABELS[tier]} by ${who} at ${when}. Previous value: ${previous}.`;
};

interface ThresholdsSkeletonProps {
  styles: ReturnType<typeof useProgramChangesPageStyles>;
  rows?: number;
  showActions?: boolean;
}

const ThresholdsSkeleton: React.FC<ThresholdsSkeletonProps> = ({
  styles,
  rows = 4,
  showActions = true,
}) => (
  <Skeleton aria-label="Loading thresholds" className={styles.skeletonBlock}>
    {Array.from({ length: rows }).map((_, rowIdx) => (
      <div key={rowIdx} className={styles.skeletonRow}>
        <div className={styles.skeletonNameCell}>
          <SkeletonItem shape="rectangle" />
        </div>
        {Array.from({ length: 4 }).map((__, i) => (
          <div key={i} className={styles.skeletonInputCell}>
            <SkeletonItem shape="rectangle" className={styles.skeletonInput} />
          </div>
        ))}
        {showActions && (
          <div className={styles.skeletonActionsCell}>
            <SkeletonItem shape="rectangle" className={styles.skeletonActionSquare} />
          </div>
        )}
      </div>
    ))}
  </Skeleton>
);

export const ThresholdsByMarketTab: React.FC<ThresholdsByMarketTabProps> = ({ isLocked }) => {
  const styles = useProgramChangesPageStyles();

  const tiers: Tier[] = useMemo(
    () =>
      TIER_KEYS.map((key) => ({
        key,
        label: TIER_LABELS[key],
        diamondClass: styles[`tierDiamond${TIER_LABELS[key]}` as keyof typeof styles],
      })),
    [styles],
  );

  const [categories, setCategoriesLocal] = useState<MarketCategory[]>([]);
  const {
    categories: ctxCategories,
    loading,
    loadError,
    refresh,
    updateThresholds,
    cancelPendingThresholds,
  } = useMarketCategories();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftThresholds, setDraftThresholds] = useState<Record<TierKey, string> | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [refreshingRowId, setRefreshingRowId] = useState<string | null>(null);
  const draftValidation = useMemo(
    () => validateThresholdDraft(draftThresholds),
    [draftThresholds],
  );
  const validationMessageId = editingId ? 'threshold-validation-message' : undefined;

  useEffect(() => {
    setCategoriesLocal(ctxCategories);
  }, [ctxCategories]);

  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const columnSizingOptions = useMemo<TableColumnSizingOptions>(() => {
    const activeIds: ColumnId[] = ['category', ...TIER_KEYS];
    if (!isLocked) activeIds.push('actions');

    const totalMin = activeIds.reduce((sum, id) => sum + columnMinWidths[id], 0);
    const totalWeight = activeIds.reduce((sum, id) => sum + columnWeights[id], 0);
    const available = Math.max(containerWidth - 2, totalMin);
    const extra = Math.max(0, available - totalMin);

    const sizing: TableColumnSizingOptions = {};
    activeIds.forEach((id) => {
      const ideal = columnMinWidths[id] + (extra * columnWeights[id]) / totalWeight;
      sizing[id] = {
        minWidth: columnMinWidths[id],
        idealWidth: Math.round(ideal),
      };
    });
    return sizing;
  }, [containerWidth, isLocked]);

  const {
    hasAnyPending,
    hasPendingCreate,
    hasPendingRename,
    hasPendingDelete,
    hasPendingThreshold,
  } = useMemo(() => {
    let anyPending = false;
    let pendingCreate = false;
    let pendingRename = false;
    let pendingDelete = false;
    let pendingThreshold = false;
    for (const c of categories) {
      if (c.pending || c.pendingThresholds) anyPending = true;
      if (c.pending?.operation === 'create') pendingCreate = true;
      else if (c.pending?.operation === 'rename') pendingRename = true;
      else if (c.pending?.operation === 'delete') pendingDelete = true;
      if (c.pendingThresholds) pendingThreshold = true;
    }
    return {
      hasAnyPending: anyPending,
      hasPendingCreate: pendingCreate,
      hasPendingRename: pendingRename,
      hasPendingDelete: pendingDelete,
      hasPendingThreshold: pendingThreshold,
    };
  }, [categories]);

  const startEdit = (category: MarketCategory) => {
    setEditingId(category.id);
    setDraftThresholds({
      silver: String(category.thresholds.silver),
      gold: String(category.thresholds.gold),
      platinum: String(category.thresholds.platinum),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraftThresholds(null);
  };

  const saveEdit = async () => {
    if (!editingId || !draftThresholds) return;
    if (!draftValidation.values) return;
    const target = categories.find((c) => c.id === editingId);
    if (!target) return;
    const next = draftValidation.values;
    setSaving(true);
    setSaveError(null);
    const rowId = editingId;
    try {
      const pendingPrev = target.pendingThresholds?.previousThresholds;
      const revertsToCommitted =
        !!pendingPrev && TIER_KEYS.every((k) => next[k] === pendingPrev[k]);
      if (revertsToCommitted) {
        await cancelPendingThresholds(rowId);
      } else {
        await updateThresholds(rowId, next);
      }
      cancelEdit();
      setRefreshingRowId(rowId);
      await refresh();
    } catch (err) {
      logApiError('ThresholdsByMarketTab.saveEdit', err);
      setSaveError(getFriendlyApiErrorMessage(err, 'Failed to save thresholds.'));
    } finally {
      setSaving(false);
      setRefreshingRowId(null);
    }
  };

  const columns:TableColumnDefinition<MarketCategory>[] = useMemo(() => {
    const cols: TableColumnDefinition<MarketCategory>[] = [
      createTableColumn<MarketCategory>({
        columnId: 'category',
        compare: (a, b) => compareString(a.name, b.name),
        renderHeaderCell: () => 'Market category',
        renderCell: () => null,
      }),
      ...tiers.map((tier) =>
        createTableColumn<MarketCategory>({
          columnId: tier.key,
          compare: (a, b) => a.thresholds[tier.key] - b.thresholds[tier.key],
          renderHeaderCell: () => (
            <span className={styles.tierHeader}>
              <DiamondFilled className={tier.diamondClass} />
              {tier.label}
            </span>
          ),
          renderCell: () => null,
        }),
      ),
    ];

    if (!isLocked) {
      cols.push(
        createTableColumn<MarketCategory>({
          columnId: 'actions',
          renderHeaderCell: () => '',
          renderCell: () => null,
        }),
      );
    }

    return cols;
  }, [tiers, isLocked, styles]);

  const renderCellContent = (columnId: ColumnId, category: MarketCategory) => {
    const isEditing = editingId === category.id;
    const pending = category.pending;
    const isPendingDelete = pending?.operation === 'delete';
    const pendingThresholds = isPendingDelete ? undefined : category.pendingThresholds;
    const isRefreshing = refreshingRowId === category.id;

    if (isRefreshing) {
      if (columnId === 'category') {
        return (
          <div className={styles.categoryNameCell}>
            <Text weight="semibold">{category.name}</Text>
          </div>
        );
      }
      if (columnId === 'actions') {
        return (
          <Skeleton aria-label="Saving">
            <SkeletonItem shape="rectangle" className={styles.skeletonActionSquare} />
          </Skeleton>
        );
      }
      return (
        <Skeleton aria-label="Saving threshold">
          <SkeletonItem shape="rectangle" className={styles.skeletonInput} />
        </Skeleton>
      );
    }

    if (columnId === 'category') {
      const nameClassName = pending?.operation === 'create'
        ? styles.categoryNamePendingCreate
        : pending?.operation === 'rename'
          ? styles.categoryNamePendingRename
          : pending?.operation === 'delete'
            ? styles.categoryNamePendingDelete
            : undefined;

      const tooltipPending: PendingMarketCategoryInfo | PendingThresholdsInfo | null =
        pending ?? pendingThresholds ?? null;
      return (
        <div>
          <div className={styles.categoryNameCell}>
            {tooltipPending && (
              <Tooltip
                relationship="description"
                content={formatPendingTooltip(tooltipPending)}
              >
                <span
                  className={styles.pendingInfoIcon}
                  role="img"
                  aria-label="Pending change details"
                  tabIndex={0}
                >
                  <InfoFilled />
                </span>
              </Tooltip>
            )}
            <Text weight="semibold" className={nameClassName}>{category.name}</Text>
          </div>
          {isEditing && draftValidation.error && (
            <div
              id={validationMessageId}
              className={styles.inlineError}
              aria-live="polite"
            >
              <Text size={200}>{draftValidation.error}</Text>
            </div>
          )}
        </div>
      );
    }

    if (columnId === 'actions') {
      if (isEditing) {
        return (
          <div className={styles.editActions}>
            <Button
              appearance="primary"
              icon={<CheckmarkRegular />}
              onClick={saveEdit}
              size="small"
              disabled={saving || !!draftValidation.error}
            >
              Save
            </Button>
            <Tooltip content="Cancel" relationship="label">
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={cancelEdit}
                aria-label="Cancel"
                size="small"
              />
            </Tooltip>
          </div>
        );
      }

      if (isPendingDelete) {
        return null;
      }
      return (
        <Tooltip content="Edit" relationship="label">
          <Button
            appearance="subtle"
            icon={<EditRegular />}
            onClick={() => startEdit(category)}
            aria-label={`Edit ${category.name}`}
            size="small"
          />
        </Tooltip>
      );
    }

    const tierKey = columnId as TierKey;
    if (isEditing && draftThresholds) {
      return (
        <Input
          value={draftThresholds[tierKey]}
          placeholder={formatCurrency(category.thresholds[tierKey])}
          onChange={(_, data) => {
            if (saveError) setSaveError(null);
            setDraftThresholds((prev) =>
              prev ? { ...prev, [tierKey]: data.value } : prev,
            );
          }}
          onBlur={() => {
            const value = parseCurrency(draftThresholds[tierKey]);
            if (value === null) return;
            setDraftThresholds((prev) =>
              prev ? { ...prev, [tierKey]: formatCurrency(value) } : prev,
            );
          }}
          aria-label={`${category.name} ${tierKey} threshold`}
          aria-invalid={!!draftValidation.error}
          aria-describedby={draftValidation.error ? validationMessageId : undefined}
          className={styles.thresholdInput}
        />
      );
    }

    const current = category.thresholds[tierKey];
    const previous = pendingThresholds?.previousThresholds[tierKey];
    const tierChanged =
      pendingThresholds !== undefined && previous !== undefined && previous !== current;
    if (tierChanged) {
      return (
        <Tooltip
          relationship="description"
          content={formatThresholdPendingTooltip(pendingThresholds!, tierKey)}
        >
          <span className={styles.thresholdCell}>
            <span className={styles.thresholdValuePrevious}>
              {formatCurrency(previous)}
            </span>
            <span className={mergeClasses(styles.thresholdValueChanged)}>
              {formatCurrency(current)}
            </span>
          </span>
        </Tooltip>
      );
    }
    return formatCurrency(current);
  };

  return (
    <div>
      <ProgramChangesLastUpdatedHeader selectedTab="thresholds-by-market" />
      <div className={styles.toolbar}>
        <Button
          appearance="subtle"
          icon={<ArrowDownloadRegular />}
          onClick={() =>
            exportToCsv<MarketCategory>({
              fileName: 'thresholds-by-market',
              columns: [
                { header: 'Market category', accessor: (c) => c.name },
                ...tiers.map((t) => ({
                  header: t.label,
                  accessor: (c: MarketCategory) => c.thresholds[t.key],
                })),
              ],
              rows: categories,
            })
          }
        >
          Export to CSV
        </Button>
      </div>

      <div className={styles.benefitsCount}>
        <Text size={200}>{categories.length} categories</Text>
      </div>

      {loadError && (
        <div className={styles.errorBar}>
          <MessageBar intent="error">
            <MessageBarBody>{loadError}</MessageBarBody>
            <MessageBarActions>
              <Button appearance="transparent" onClick={() => void refresh()}>
                Retry
              </Button>
            </MessageBarActions>
          </MessageBar>
        </div>
      )}

      {saveError && (
        <div className={styles.errorBar}>
          <MessageBar intent="error">
            <MessageBarBody>{saveError}</MessageBarBody>
            <MessageBarActions>
              <Button appearance="transparent" onClick={() => setSaveError(null)}>
                Dismiss
              </Button>
            </MessageBarActions>
          </MessageBar>
        </div>
      )}

      <div ref={gridContainerRef} className={styles.gridContainer}>
        {hasAnyPending ? (
          <div className={styles.pendingLegend} role="note" aria-label="Pending changes legend">
            <span><strong>Pending changes legend:</strong></span>
            {hasPendingCreate ? (
              <span className={styles.pendingLegendItem}>
                <span className={styles.categoryNameSwatchCreate} aria-hidden>Aa</span>
                New category
              </span>
            ) : null}
            {hasPendingRename ? (
              <span className={styles.pendingLegendItem}>
                <span className={styles.categoryNameSwatchRename} aria-hidden>Aa</span>
                Renamed category
              </span>
            ) : null}
            {hasPendingDelete ? (
              <span className={styles.pendingLegendItem}>
                <span className={styles.categoryNameSwatchDelete} aria-hidden>Aa</span>
                Category to be removed
              </span>
            ) : null}
            {hasPendingThreshold ? (
              <>
                <span className={styles.pendingLegendItem}>
                  <span
                    className={mergeClasses(styles.pendingLegendSwatch, styles.countryBadgeAdded)}
                    aria-hidden
                  />
                  Pending threshold value
                </span>
                <span className={styles.pendingLegendItem}>
                  <span className={styles.thresholdValuePrevious} aria-hidden>$0</span>
                  Previous value
                </span>
              </>
            ) : null}
          </div>
        ) : null}
        {loading && categories.length === 0 ? (
          <ThresholdsSkeleton styles={styles} showActions={!isLocked} />
        ) : (
          <DataGrid
          key={`grid-${Math.round(containerWidth / 40)}-${isLocked ? 'l' : 'u'}`}
          items={categories}
          columns={columns}
          sortable
          resizableColumns
          resizableColumnsOptions={{ autoFitColumns: false }}
          columnSizingOptions={columnSizingOptions}
          getRowId={(item) => (item as MarketCategory).id}
          size="medium"
        >
          <DataGridHeader>
            <DataGridRow>
              {({ renderHeaderCell }) => (
                <DataGridHeaderCell focusMode="none">{renderHeaderCell()}</DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<MarketCategory>>
            {({ item, rowId }) => (
              <DataGridRow<MarketCategory>
                key={rowId}
                className={editingId === item.id ? styles.benefitRowEditing : undefined}
              >
                {({ columnId }) => (
                  <DataGridCell>
                    <TableCellLayout>
                      {renderCellContent(columnId as ColumnId, item)}
                    </TableCellLayout>
                  </DataGridCell>
                )}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
        )}
      </div>
    </div>
  );
};
